import { MAX_RAW_IMAGE_BYTES } from '../../../domains/media/utils/imageProcessor';
import { Logger } from '../../../utils/logger';

const CLIPBOARD_DEBUG_PREFIX = 'CanvasClipboardImage';

const SUPPORTED_CLIPBOARD_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

function extensionForMime(mime: string): string {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/jpeg') return 'jpg';
  return 'webp';
}

export async function pickBestFileFromNavigatorClipboardItems(
  items: ClipboardItem[],
): Promise<File | null> {
  type Candidate = { type: string; blob: Blob };
  const collected: Candidate[] = [];
  for (const item of items) {
    for (const type of item.types) {
      const normalized = (type || '').toLowerCase();
      if (!SUPPORTED_CLIPBOARD_TYPES.has(normalized)) continue;
      const blob = await item.getType(type);
      collected.push({ type: normalized, blob });
    }
  }
  if (collected.length === 0) {
    Logger.debug(CLIPBOARD_DEBUG_PREFIX, 'navigator.clipboard.read: no png/jpeg/webp blobs', {
      itemCount: items.length,
      typesPerItem: items.map((it) => [...it.types]),
    });
    return null;
  }

  const candidateSummary = collected.map((c) => ({ type: c.type, size: c.blob.size }));
  const underLimit = collected.filter((c) => c.blob.size <= MAX_RAW_IMAGE_BYTES);
  const pool = underLimit.length > 0 ? underLimit : collected;
  pool.sort((a, b) => a.blob.size - b.blob.size);
  const pick = pool[0];
  Logger.debug(CLIPBOARD_DEBUG_PREFIX, 'navigator.clipboard.read: picked representation', {
    itemCount: items.length,
    candidates: candidateSummary,
    underLimitCount: underLimit.length,
    limitBytes: MAX_RAW_IMAGE_BYTES,
    chosenType: pick.type,
    chosenSize: pick.blob.size,
    usedFallbackOverLimit: underLimit.length === 0,
  });
  const ext = extensionForMime(pick.type);
  return new File([pick.blob], `clipboard.${ext}`, { type: pick.type });
}
